import { MONITOR_LOCATIONS } from '@/lib/check-locations'
// Subscription tiers shown on /pricing and in the in-app plan picker.
// The iOS app keeps a copy of the plan features for its App Store Plans screen
// (uptrack-mobile lib/features/billing/apple_plans.dart). Change both together.
export const tiers = [
  {
    name: 'Pro',
    plan: 'pro',
    monthlyPrice: 3,
    annualMonthlyPrice: 2.5,
    annualTotalPrice: 30,
    savings: 17,
    description: 'For serious monitoring on a budget.',
    features: [
      { name: '50 monitors — all at 30s', included: true },
      { name: MONITOR_LOCATIONS.plan.feature.pro, included: true },
      { name: 'Email, Slack, Discord, Telegram, webhook alerts', included: true },
      { name: '5 status pages', included: true },
      { name: '3 team members', included: true },
      { name: '2 years history', included: true },
      { name: 'Keyword monitoring', included: true },
      { name: 'Custom domain', included: false },
    ],
  },
  {
    name: 'Team',
    plan: 'team',
    monthlyPrice: 20,
    annualMonthlyPrice: 15,
    annualTotalPrice: 180,
    savings: 25,
    description: 'For teams that run critical infrastructure.',
    features: [
      { name: '200 monitors — all at 30s', included: true },
      { name: MONITOR_LOCATIONS.plan.feature.team, included: true },
      { name: 'Unlimited alert channels', included: true },
      { name: 'Unlimited status pages', included: true },
      { name: '5 team members', included: true },
      { name: '2 years history', included: true },
      { name: 'Escalation policies', included: true },
      { name: 'Custom domain + theming', included: true },
      { name: 'SSO / Whitelabel', included: false },
    ],
  },
  {
    name: 'Business',
    plan: 'business',
    monthlyPrice: 79,
    annualMonthlyPrice: 49,
    annualTotalPrice: 588,
    savings: 38,
    description: 'For organizations that need it all.',
    features: [
      { name: '1,000 monitors — all at 30s', included: true },
      { name: MONITOR_LOCATIONS.plan.feature.business, included: true },
      { name: 'Unlimited alert channels', included: true },
      { name: 'Unlimited status pages', included: true },
      { name: '15 team members', included: true },
      { name: '5 years history', included: true },
      { name: 'Priority support', included: true },
      { name: 'Whitelabel + custom sender', included: true },
      { name: 'SSO / SAML', included: true },
    ],
  },
] as const

export type PaidPlan = (typeof tiers)[number]['plan']
export type BillingInterval = 'monthly' | 'annual'

export const PAID_PLANS: readonly PaidPlan[] = tiers.map((t) => t.plan)

export function isPaidPlan(v: unknown): v is PaidPlan {
  return typeof v === 'string' && (PAID_PLANS as readonly string[]).includes(v)
}

/** `?plan=` on /signup and /login: a paid plan (checkout follows) or `free` (no checkout). */
export type SignupPlan = PaidPlan | 'free'

export function isSignupPlan(v: unknown): v is SignupPlan {
  return v === 'free' || isPaidPlan(v)
}

export function isBillingInterval(v: unknown): v is BillingInterval {
  return v === 'monthly' || v === 'annual'
}

/** The yearly saving of a plan, in whole percent, from its monthly and annual prices. */
export function annualSavingPercent(plan: PaidPlan): number {
  const tier = tiers.find((t) => t.plan === plan)
  if (!tier) return 0
  return Math.round((1 - tier.annualMonthlyPrice / tier.monthlyPrice) * 100)
}

/** The largest yearly saving of any plan. Copy that says "up to" reads this. */
export const MAX_ANNUAL_SAVING_PERCENT = Math.max(...PAID_PLANS.map(annualSavingPercent))
